/**
 * Petstyle24 – Voraussichtliche Lieferung als Datum (E33; Gesamtplan §6 Nr. 1, Et. 6; KI-33).
 *
 * Einzige Stelle, an der das Datum berechnet wird. Rohwerte und Texte liefert layout/theme.liquid als
 * JSON-Block #ps-delivery-config (Einstellungen cart_eta_* ohne Standardwert, Land aus Shopify,
 * Texte aus der Locale). Ziele sind Elemente mit data-ps-eta:
 *   "text" – der Text des Elements wird „<Label><Trenner><Datum oder Spanne>“ (Produktseite, Trust-Leiste)
 *   "row"  – [data-ps-eta-label] bekommt das Label, [data-ps-eta-value] Datum oder Spanne (Warenkorb)
 * Verfügbarkeit: eigenes data-ps-eta-available, sonst das erste data-ps-eta-available im nächsten
 * [data-ps-eta-scope] (Produktseite: der Kaufbereich, den Shopify beim Größenwechsel neu liefert).
 * Nur "true" zählt; fehlt die Angabe, bleibt der bisherige Text.
 *
 * Regeln
 * - Deutsche Zeit über Intl (Zeitzone Europe/Berlin, auch über die Zeitumstellung); der Kalender rechnet
 *   nur mit UTC-Tagesnummern, nie mit der Ortszeit des Geräts.
 * - Versandtag: Mo–Fr, kein Feiertag (die 9 bundesweiten und Nordrhein-Westfalen als Versandort),
 *   nicht der 24.12. und 31.12.
 * - Zustelltag: Mo–Fr, kein Feiertag DE + NRW; bei Land AT zusätzlich kein österreichischer Feiertag.
 * - Start heute, wenn heute Versandtag ist und die Uhrzeit vor dem Versandschluss liegt (genau zur
 *   Schlusszeit zählt schon der nächste Versandtag); dann Bearbeitungszeit in Versandtagen, dann die
 *   Laufzeit (mindestens/höchstens) in Zustelltagen. Mindestens = höchstens ergibt ein einzelnes Datum.
 * - Land AT, wenn Shopify Österreich meldet, sonst DE. Keine Ausweichwerte: Ist ein Wert leer oder
 *   ungültig, bleibt überall der bisherige Text.
 * - Kein Netz, kein Browser-Speicher, kein HTML-Einfügen: nur textContent, geschrieben nur bei Änderung.
 *
 * Neu berechnet wird beim Start, sobald markierte Elemente neu eingesetzt werden (Warenkorb nach jedem
 * Neuaufbau, Kaufbereich nach dem Größenwechsel – im selben Durchlauf, vor dem nächsten Zeichnen), bei
 * der Rückkehr aus dem Zurück-Zwischenstand (pageshow) und wenn der Tab wieder sichtbar wird.
 */
(() => {
  'use strict';

  const CONFIG_ID = 'ps-delivery-config';
  const TIME_ZONE = 'Europe/Berlin';
  const DAY_MS = 86400000;
  const MAX_DAYS = 30;
  const MAX_STEPS = 60;
  const CUTOFF_RE = /^([01]?\d|2[0-3]):([0-5]\d)$/;
  const TARGETS = '[data-ps-eta]';
  const WATCHED = '[data-ps-eta], [data-ps-eta-available]';

  /* ---------- Einstellungen (nur prüfen, nie ergänzen) ---------- */
  const readConfig = () => {
    const node = document.getElementById(CONFIG_ID);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return null;
    }
  };

  const toInteger = (value) => {
    if (typeof value === 'number') return Number.isInteger(value) ? value : NaN;
    if (typeof value === 'string' && /^\s*\d+\s*$/.test(value)) return parseInt(value, 10);
    return NaN;
  };

  const textOf = (value) => (typeof value === 'string' && value.trim() !== '' ? value : null);

  const partsOf = (value, size) => {
    if (typeof value !== 'string') return null;
    const parts = value.split('|');
    return parts.length === size && parts.every((part) => part.trim() !== '') ? parts : null;
  };

  const settingsFrom = (config) => {
    if (!config || typeof config !== 'object') return null;
    const country = config.country === 'AT' ? 'AT' : 'DE';

    const processingDays = toInteger(config.processingDays);
    if (!(processingDays >= 0 && processingDays <= MAX_DAYS)) return null;

    const cutoff = typeof config.cutoff === 'string' ? CUTOFF_RE.exec(config.cutoff.trim()) : null;
    if (!cutoff) return null;

    const transit = config.transit && Array.isArray(config.transit[country]) ? config.transit[country] : null;
    if (!transit || transit.length !== 2) return null;
    const min = toInteger(transit[0]);
    const max = toInteger(transit[1]);
    if (!(min >= 1 && min <= MAX_DAYS && max >= min && max <= MAX_DAYS)) return null;

    const strings = config.strings && typeof config.strings === 'object' ? config.strings : {};
    const label = textOf(strings.label);
    const separator = textOf(strings.separator);
    const dateTemplate = textOf(strings.date);
    const rangeTemplate = textOf(strings.range);
    const weekdays = partsOf(strings.weekdays, 7);
    const months = partsOf(strings.months, 12);
    if (!label || !separator || !dateTemplate || !rangeTemplate || !weekdays || !months) return null;

    return {
      country,
      processingDays,
      cutoffMinutes: parseInt(cutoff[1], 10) * 60 + parseInt(cutoff[2], 10),
      min,
      max,
      label,
      separator,
      dateTemplate,
      rangeTemplate,
      weekdays,
      months,
    };
  };

  /* ---------- Kalender: UTC-Tagesnummern ---------- */
  const dayNumber = (year, month, day) => Math.round(Date.UTC(year, month - 1, day) / DAY_MS);
  const calendarDate = (day) => new Date(day * DAY_MS);

  // Jetzt in deutscher Zeit: Tagesnummer und Minuten seit Mitternacht; ohne Zeitzonen-Unterstützung null
  const berlinNow = () => {
    let parts;
    try {
      parts = new Intl.DateTimeFormat('de-DE', {
        timeZone: TIME_ZONE,
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }).formatToParts(new Date());
    } catch (error) {
      return null;
    }
    const valueOf = (type) => {
      const part = parts.find((entry) => entry.type === type);
      return part ? parseInt(part.value, 10) : NaN;
    };
    const year = valueOf('year');
    const month = valueOf('month');
    const day = valueOf('day');
    const hour = valueOf('hour') % 24;
    const minute = valueOf('minute');
    if (![year, month, day, hour, minute].every(Number.isFinite)) return null;
    return { day: dayNumber(year, month, day), minutes: hour * 60 + minute };
  };

  // Ostersonntag (gregorianisch, Verfahren nach Meeus/Jones/Butcher)
  const easterDay = (year) => {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31);
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return dayNumber(year, month, day);
  };

  const years = new Map();
  const yearDays = (year) => {
    let entry = years.get(year);
    if (entry) return entry;
    const easter = easterDay(year);
    const on = (month, day) => dayNumber(year, month, day);
    entry = {
      // 9 bundesweite Feiertage, dazu Nordrhein-Westfalen (Versandort): Fronleichnam, Allerheiligen
      germany: new Set([
        on(1, 1), easter - 2, easter + 1, on(5, 1), easter + 39, easter + 50, on(10, 3), on(12, 25), on(12, 26),
        easter + 60, on(11, 1),
      ]),
      // 13 österreichische Feiertage (nur für die Zustellung bei Land AT)
      austria: new Set([
        on(1, 1), on(1, 6), easter + 1, on(5, 1), easter + 39, easter + 50, easter + 60, on(8, 15), on(10, 26),
        on(11, 1), on(12, 8), on(12, 25), on(12, 26),
      ]),
      // kein Versand
      closed: new Set([on(12, 24), on(12, 31)]),
    };
    years.set(year, entry);
    return entry;
  };

  const isMondayToFriday = (day) => {
    const weekday = calendarDate(day).getUTCDay();
    return weekday !== 0 && weekday !== 6;
  };

  const isShippingDay = (day) => {
    if (!isMondayToFriday(day)) return false;
    const entry = yearDays(calendarDate(day).getUTCFullYear());
    return !entry.germany.has(day) && !entry.closed.has(day);
  };

  const isDeliveryDay = (day, country) => {
    if (!isMondayToFriday(day)) return false;
    const entry = yearDays(calendarDate(day).getUTCFullYear());
    if (entry.germany.has(day)) return false;
    return !(country === 'AT' && entry.austria.has(day));
  };

  const nextDay = (day, test) => {
    let current = day;
    for (let step = 0; step < MAX_STEPS; step += 1) {
      current += 1;
      if (test(current)) return current;
    }
    return null;
  };

  const advance = (day, count, test) => {
    let current = day;
    for (let index = 0; index < count && current !== null; index += 1) current = nextDay(current, test);
    return current;
  };

  const estimate = (settings, now) => {
    const start = isShippingDay(now.day) && now.minutes < settings.cutoffMinutes
      ? now.day
      : nextDay(now.day, isShippingDay);
    const shipped = start === null ? null : advance(start, settings.processingDays, isShippingDay);
    if (shipped === null) return null;
    const delivers = (day) => isDeliveryDay(day, settings.country);
    const from = advance(shipped, settings.min, delivers);
    const to = from === null ? null : advance(from, settings.max - settings.min, delivers);
    return from === null || to === null ? null : { from, to };
  };

  /* ---------- Text ---------- */
  const fill = (template, values) => template.replace(/\{(\w+)\}/g, (match, key) => (
    Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match
  ));

  const formatDay = (settings, day) => {
    const date = calendarDate(day);
    return fill(settings.dateTemplate, {
      weekday: settings.weekdays[date.getUTCDay()],
      day: String(date.getUTCDate()),
      month: settings.months[date.getUTCMonth()],
    });
  };

  const formatRange = (settings, result) => (result.from === result.to
    ? formatDay(settings, result.from)
    : fill(settings.rangeTemplate, { from: formatDay(settings, result.from), to: formatDay(settings, result.to) }));

  /* ---------- Start ---------- */
  const settings = settingsFrom(readConfig());
  const root = document.body;
  if (!settings || !root) return;

  // Servertext je Knoten, damit er bei nicht verfügbarer Variante zurückkommt
  const originals = new WeakMap();

  const write = (node, value) => {
    if (!node) return;
    if (!originals.has(node)) originals.set(node, node.textContent);
    if (node.textContent !== value) node.textContent = value;
  };

  const restore = (node) => {
    if (!node || !originals.has(node)) return;
    const value = originals.get(node);
    if (node.textContent !== value) node.textContent = value;
  };

  const isAvailable = (target) => {
    let flag = target.getAttribute('data-ps-eta-available');
    if (flag === null) {
      const scope = target.closest('[data-ps-eta-scope]');
      const holder = scope ? scope.querySelector('[data-ps-eta-available]') : null;
      flag = holder ? holder.getAttribute('data-ps-eta-available') : null;
    }
    return flag === 'true';
  };

  const update = () => {
    const targets = root.querySelectorAll(TARGETS);
    if (!targets.length) return;
    const now = berlinNow();
    const result = now ? estimate(settings, now) : null;
    const value = result ? formatRange(settings, result) : null;
    targets.forEach((target) => {
      const show = value !== null && isAvailable(target);
      const kind = target.getAttribute('data-ps-eta');
      if (kind === 'row') {
        const label = target.querySelector('[data-ps-eta-label]');
        const cell = target.querySelector('[data-ps-eta-value]');
        if (show && label && cell) {
          write(label, settings.label);
          write(cell, value);
        } else {
          restore(label);
          restore(cell);
        }
      } else if (kind === 'text') {
        if (show) write(target, settings.label + settings.separator + value);
        else restore(target);
      }
    });
  };

  // Nur neu eingesetzte Elemente mit Markierung zählen; eigene Textänderungen (Textknoten) lösen nichts aus
  const isRelevant = (node) => node.nodeType === 1 && (node.matches(WATCHED) || node.querySelector(WATCHED) !== null);

  const observer = new MutationObserver((records) => {
    if (records.some((record) => Array.prototype.some.call(record.addedNodes, isRelevant))) update();
  });

  update();
  observer.observe(root, { childList: true, subtree: true });
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) update();
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') update();
  });
})();
